import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, LineChart, FileText, Cpu, HeartPulse, Building2, BrainCircuit, Landmark, MapPin } from "lucide-react";
import { useI18n } from "../context/I18nContext";
import { Button } from "../components/ui/button";
import { RiskNote } from "../components/layout/RiskDisclaimer";
import PressBanner from "../components/layout/PressBanner";

const industryIcon = {
  Santé: HeartPulse, Health: HeartPulse,
  Assurance: Building2, Insurance: Building2,
  "IA Réglementée": BrainCircuit, "AI": BrainCircuit,
  Médical: Landmark, Medical: Landmark,
};

export default function Home() {
  const { t, lang } = useI18n();
  const industries = lang === "fr"
    ? ["Santé", "Assurance", "IA Réglementée", "Médical"]
    : ["Health", "Insurance", "Regulated AI", "Medical"];

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-slate-200">
        <div className="absolute inset-0 bg-grid-slate opacity-70 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-20 pb-24 sm:pt-28 sm:pb-32 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-slate-600 bg-white border border-slate-200 rounded-sm px-3 py-1.5 mb-8">
              <span className="live-dot" /> {t.hero.eyebrow}
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-[68px] font-black tracking-tight leading-[1.02] text-slate-900" data-testid="hero-title">
              {t.hero.title}
            </h1>
            <p className="mt-8 text-lg text-slate-600 max-w-2xl leading-relaxed" data-testid="hero-subtitle">
              {t.hero.subtitle}
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-3">
              <Button asChild size="lg" className="rounded-sm bg-slate-900 hover:bg-slate-800 h-12 px-6" data-testid="hero-cta-primary">
                <Link to="/simulator">
                  {t.hero.cta_primary} <ArrowRight className="h-4 w-4 ml-2" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="rounded-sm h-12 px-6" data-testid="hero-cta-secondary">
                <Link to="/invest">{t.hero.cta_secondary}</Link>
              </Button>
            </div>
            <div className="mt-8">
              <RiskNote />
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="bg-white border border-slate-200 rounded-sm p-6 shadow-sm">
              <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-bold">KPI · Scénario central</div>
              <div className="mt-4 grid grid-cols-2 gap-5">
                {[
                  { k: t.invest.target, v: "12%", sub: "/ an" },
                  { k: t.invest.min, v: "1 000€", sub: "ticket" },
                  { k: t.invest.horizon, v: "36", sub: "mois" },
                  { k: "Uptime", v: "99.9%", sub: "cible" },
                ].map((kpi, i) => (
                  <div key={i} className="border-t border-slate-200 pt-4">
                    <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500">{kpi.k}</div>
                    <div className="mt-1 flex items-baseline gap-1">
                      <div className="font-mono text-3xl font-semibold text-slate-900">{kpi.v}</div>
                      <div className="text-xs text-slate-500">{kpi.sub}</div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-6 pt-5 border-t border-slate-200 space-y-3">
                {t.hero.proofs.map((p, i) => (
                  <div key={i} className="flex items-center justify-between text-sm">
                    <span className="text-slate-600">{p.k}</span>
                    <span className="font-medium text-slate-900">{p.v}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PRESS / SOCIAL PROOF */}
      <PressBanner />

      {/* WHY NOW */}
      <section className="section">
        <div className="container-page grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="label-divider mb-6">01 · {t.home.why_now_title}</div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">{t.home.why_now_title}</h2>
          </div>
          <div className="lg:col-span-8">
            <p className="text-lg text-slate-700 leading-relaxed">{t.home.why_now_body}</p>
            <div className="mt-10 grid md:grid-cols-3 gap-6">
              {[
                { t: "+35% /an", d: lang === "fr" ? "Croissance demande GPU Europe 2024-2027" : "Europe GPU demand growth 2024-2027" },
                { t: "< 5%", d: lang === "fr" ? "Capacité GPU hébergée sur sol EU" : "GPU capacity hosted on EU soil" },
                { t: "5 ans", d: lang === "fr" ? "Horizon contractuel moyen opérateurs" : "Average operator contract horizon" },
              ].map((k, i) => (
                <div key={i} className="p-6 border border-slate-200 bg-white rounded-sm">
                  <div className="font-mono text-2xl font-semibold text-slate-900">{k.t}</div>
                  <div className="text-sm text-slate-600 mt-2">{k.d}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* MODEL */}
      <section className="section bg-slate-900 text-white">
        <div className="container-page">
          <div className="grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <div className="label-divider mb-6 !text-slate-400">02 · {t.home.revenue_title}</div>
              <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">{t.home.revenue_title}</h2>
              <p className="mt-5 text-slate-300 leading-relaxed">{t.home.revenue_body}</p>
              <Button asChild variant="outline" className="mt-8 rounded-sm bg-transparent text-white border-slate-600 hover:bg-slate-800 hover:text-white" data-testid="home-model-cta">
                <Link to="/model">
                  {t.nav.model} <ArrowRight className="h-4 w-4 ml-2" />
                </Link>
              </Button>
            </div>
            <div className="lg:col-span-7 grid grid-cols-2 gap-px bg-slate-800 border border-slate-800">
              {t.model.steps.map((s, i) => (
                <div key={i} className="p-6 bg-slate-900">
                  <div className="font-mono text-xs text-emerald-400">{s.n}</div>
                  <div className="mt-3 font-display font-semibold text-white">{s.t}</div>
                  <div className="mt-2 text-sm text-slate-400 leading-relaxed">{s.d}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section className="section">
        <div className="container-page">
          <div className="label-divider mb-6">03 · {t.home.industries_title}</div>
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 max-w-3xl">
            {t.home.sovereignty_title}
          </h2>
          <p className="mt-5 text-slate-700 max-w-3xl leading-relaxed">{t.home.sovereignty_body}</p>

          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {industries.map((n, i) => {
              const Icon = industryIcon[n] || Cpu;
              return (
                <div key={i} className="bg-white border border-slate-200 rounded-sm p-6 hover:-translate-y-0.5 hover:shadow-md transition-all" data-testid={`industry-${i}`}>
                  <Icon className="h-6 w-6 text-emerald-700 mb-5" strokeWidth={1.5} />
                  <div className="font-display font-semibold text-slate-900">{n}</div>
                  <div className="text-xs text-slate-500 mt-1 uppercase tracking-[0.15em]">Regulated sector</div>
                </div>
              );
            })}
          </div>

          {/* Datacenter locations */}
          <div className="mt-10 bg-slate-50 border border-slate-200 rounded-sm p-6 sm:p-8 grid md:grid-cols-12 gap-6 items-center" data-testid="datacenter-locations">
            <div className="md:col-span-4 flex items-center gap-3">
              <div className="shrink-0 w-10 h-10 rounded-sm bg-slate-900 text-emerald-400 flex items-center justify-center">
                <MapPin className="h-5 w-5" strokeWidth={1.7} />
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-bold">
                  {lang === "fr" ? "Localisation des actifs" : "Asset locations"}
                </div>
                <div className="font-display text-base font-semibold text-slate-900 mt-1">
                  {lang === "fr" ? "Hébergement souverain UE" : "EU sovereign hosting"}
                </div>
              </div>
            </div>
            <div className="md:col-span-8">
              <p className="text-sm text-slate-700 leading-relaxed">
                {lang === "fr"
                  ? "L'ensemble des GPU détenus par nos SPV est hébergé dans des datacenters partenaires Tier III+ situés exclusivement en"
                  : "All GPUs held by our SPVs are hosted in Tier III+ partner datacenters located exclusively in"}{" "}
                <span className="inline-flex flex-wrap gap-1.5 align-middle">
                  <span className="inline-flex items-center gap-1.5 bg-white border border-slate-200 rounded-sm px-2 py-0.5 text-xs font-semibold text-slate-900" data-testid="dc-flag-fr">
                    <span aria-hidden="true">🇫🇷</span> France
                  </span>
                  <span className="inline-flex items-center gap-1.5 bg-white border border-slate-200 rounded-sm px-2 py-0.5 text-xs font-semibold text-slate-900" data-testid="dc-flag-de">
                    <span aria-hidden="true">🇩🇪</span> {lang === "fr" ? "Allemagne" : "Germany"}
                  </span>
                  <span className="inline-flex items-center gap-1.5 bg-white border border-slate-200 rounded-sm px-2 py-0.5 text-xs font-semibold text-slate-900" data-testid="dc-flag-lu">
                    <span aria-hidden="true">🇱🇺</span> {lang === "fr" ? "Luxembourg" : "Luxembourg"}
                  </span>
                </span>{" "}
                {lang === "fr"
                  ? "— conformité RGPD, NIS2 et exigences de souveraineté européenne, sans transfert de données hors UE."
                  : "— GDPR, NIS2 and EU sovereignty compliance, with no data transfer outside the EU."}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA BAND */}
      <section className="border-y border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8">
            <h3 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
              {lang === "fr" ? "Prêt à modéliser votre allocation ?" : "Ready to model your allocation?"}
            </h3>
            <p className="text-slate-600 mt-2">
              {lang === "fr"
                ? "Simulez, qualifiez votre profil et souscrivez en moins de 7 jours."
                : "Simulate, qualify your profile and subscribe in under 7 days."}
            </p>
          </div>
          <div className="md:col-span-4 flex md:justify-end gap-3">
            <Button asChild size="lg" className="rounded-sm bg-slate-900 hover:bg-slate-800" data-testid="cta-band-simulator">
              <Link to="/simulator">{t.hero.cta_primary} <ArrowRight className="h-4 w-4 ml-2" /></Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
